import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusText } from "../app/SettingsSection";
import { useSession } from "../app/session";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDocSelector } from "../core/useDocSelector";
import { readPlaces } from "../signals/settingsOps";
import { Checkbox, Chip } from "../ui";
import { useCircle } from "./useCircle";

interface Share {
  on: boolean;
  places: string[];
  people: string[];
}

interface Present {
  profileId: string;
  name: string;
  color: string | null;
  place: string;
  since: string;
}

const NONE: Share = { on: false, places: [], people: [] };

/**
 * Presence in a shared space (P1): who is at which shared place right now (only those who share it with me, and
 * only at the places they chose), and my own sharing — off by default; places and people chosen; one switch
 * stops it and nothing is kept.
 */
export function PresenceSettings({ workspaceId }: { workspaceId: string }) {
  const { t, i18n } = useTranslation();
  const { handle } = useCoreDoc();
  const circle = useCircle(handle);
  const me = useSession((s) => s.session?.profile);
  const placesJson = useDocSelector(handle, (doc) =>
    JSON.stringify(readPlaces(doc).map((p) => ({ id: p.id, name: p.name }))),
  );
  const places = JSON.parse(placesJson ?? "[]") as { id: string; name: string }[];
  const [share, setShare] = useState<Share>(NONE);
  const [present, setPresent] = useState<Present[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    void fetch("/api/internal/presence/near", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { on: false }))
      .then((b: { on: boolean }) => setNear(b.on));
  }, []);
  const saveNear = async (on: boolean) => {
    const r = await fetch("/api/internal/presence/near", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ on }),
    });
    if (r.ok) setNear(((await r.json()) as { on: boolean }).on);
  };
  const q = `workspace=${encodeURIComponent(workspaceId)}`;

  const refresh = useCallback(() => {
    void fetch(`/api/internal/presence/share?${q}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : NONE))
      .then((b: Share) => setShare(b));
    void fetch(`/api/internal/presence?${q}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { present: [] }))
      .then((b: { present: Present[] }) => setPresent(b.present));
  }, [q]);
  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const save = async (next: Share) => {
    const r = await fetch("/api/internal/presence/share", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspace: workspaceId, ...next }),
    });
    const b = (await r.json()) as Share & { error?: string };
    if (!r.ok) {
      setError(t(`errors.${b.error}`, { defaultValue: t("error.generic") }));
      return;
    }
    setError(null);
    setShare(b);
  };
  const others = circle.members.filter((m) => m.id !== me?.id);
  const time = new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" });
  const toggle = (list: string[], id: string, on: boolean) =>
    on ? [...list, id] : list.filter((x) => x !== id);
  // the first switch-on shares every shared place with everyone; the person narrows it down from there
  const switchOn = (on: boolean) =>
    void save(on ? { on, places: places.map((p) => p.id), people: [] } : NONE);

  return (
    <div className="flex flex-col gap-2" data-testid="presence">
      <h3 className="text-[12.5px] text-neutral-500">{t("presence.title")}</h3>
      {present.length === 0 ? (
        <p className="text-[12.5px] text-neutral-500" data-testid="presence-none">
          {t("presence.nobody")}
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="presence-list">
          {present.map((p) => (
            <Chip key={p.profileId} tone={p.color}>
              {t("presence.at", { name: p.name, place: p.place, since: time.format(new Date(p.since)) })}
            </Chip>
          ))}
        </div>
      )}

      {places.length === 0 ? (
        <p className="text-[12.5px] text-neutral-500">{t("presence.noPlaces")}</p>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox
              checked={share.on}
              onChange={switchOn}
              label={t("presence.share")}
              className="shrink-0"
            />
            <span data-testid="presence-share">{t("presence.share")}</span>
          </label>
          <p className="text-[12px] text-neutral-500">{t("presence.shareHint")}</p>
          {share.on && (
            <>
              <fieldset className="flex flex-wrap gap-x-4 gap-y-1" data-testid="presence-places">
                <legend className="mb-1 text-[12px] text-neutral-500">{t("presence.places")}</legend>
                {places.map((p) => (
                  <label key={p.id} className="flex items-center gap-1.5 text-[13px]">
                    <Checkbox
                      checked={share.places.includes(p.id)}
                      label={p.name}
                      onChange={(v) => void save({ ...share, places: toggle(share.places, p.id, v) })}
                    />
                    {p.name}
                  </label>
                ))}
              </fieldset>
              <fieldset className="flex flex-wrap gap-x-4 gap-y-1" data-testid="presence-people">
                <legend className="mb-1 text-[12px] text-neutral-500">{t("presence.people")}</legend>
                <label className="flex items-center gap-1.5 text-[13px]">
                  <Checkbox
                    checked={share.people.length === 0}
                    label={t("presence.everyone")}
                    onChange={(v) => v && void save({ ...share, people: [] })}
                  />
                  {t("presence.everyone")}
                </label>
                {others.map((m) => (
                  <label key={m.id} className="flex items-center gap-1.5 text-[13px]">
                    <Checkbox
                      checked={share.people.includes(m.id)}
                      label={m.name}
                      onChange={(v) => void save({ ...share, people: toggle(share.people, m.id, v) })}
                    />
                    {m.name}
                  </label>
                ))}
              </fieldset>
              <StatusText tone="quiet">
                {share.people.length === 0
                  ? t("presence.summaryAll", { n: share.places.length })
                  : t("presence.summarySome", { n: share.places.length, people: share.people.length })}
              </StatusText>
            </>
          )}
          {error && (
            <p className="text-[12.5px] text-err" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 text-[13px]">
        <Checkbox checked={near} onChange={(v) => void saveNear(v)} label={t("presence.near")} />
        <span data-testid="presence-near">{t("presence.near")}</span>
      </label>
      <p className="text-[12px] text-neutral-500">{t("presence.nearHint")}</p>
    </div>
  );
}
